import { ActivityIndicator, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem, ReportsListItemText } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import {
  useRoadSenseLifecycleSites,
  RoadSenseLifecycleNotFoundError,
  RoadSenseLifecycleNotReadyError,
} from "@src/features/roadsense/hooks/useRoadSenseLifecycleSites";
import type { RoadSenseListingSite } from "@visioni/a3s-reports/client-messages";

/**
 * Expo Router params are `string | string[]` (a segment can repeat). Path
 * segments never repeat in practice; this collapses to the first value (or
 * `undefined`) so lookups and href building only deal with a plain string.
 */
function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

/**
 * RoadSense **site list for one lifecycle** (`…/reports/roadsense/report/<reportId>`).
 *
 * A RoadSense report run produces one render `.json` + one `.xlsx` per
 * monitored site; that per-site list lives on the report row's
 * `listingSummary`. This screen fetches the row and lists those sites; picking
 * one opens the report (`…/report/<reportId>/site/<siteId>`).
 */
export default function RoadSenseLifecycleSiteListScreen() {
  const params = useLocalSearchParams<{ tenantId?: string; reportId?: string }>();
  const tenantId = firstParam(params.tenantId);
  const reportId = firstParam(params.reportId);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          RoadSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view its sites.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      {reportId && tenantId ?
        <LiveSiteList tenantId={tenantId} reportId={reportId} />
      : <Header
          title="Invalid report"
          subtitle="This URL is missing a report id. Pick a report from the list to continue."
        />
      }
    </ScrollView>
  );
}

function LiveSiteList({ tenantId, reportId }: { tenantId: string; reportId: string }) {
  const { customerName, sites, isLoading, isError, error, isTenantPending } = useRoadSenseLifecycleSites(reportId);

  return (
    <View gap="6xl">
      <Header title={customerName ?? "RoadSense report"} subtitle="Pick a site to view its report." />
      <ReportsSection title="Sites">
        <SiteListBody
          tenantId={tenantId}
          reportId={reportId}
          sites={sites}
          isLoading={isLoading || isTenantPending}
          isError={isError}
          error={error}
        />
      </ReportsSection>
    </View>
  );
}

interface SiteListBodyProps {
  tenantId: string;
  reportId: string;
  sites: readonly RoadSenseListingSite[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Renders the live site list body. Loading, the readiness states (not
 * found/not ready), generic errors and the empty state are surfaced inline so
 * the section chrome stays stable across refetches.
 */
function SiteListBody({ tenantId, reportId, sites, isLoading, isError, error }: SiteListBodyProps) {
  if (isLoading && sites.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          Loading sites…
        </Text>
      </View>
    );
  }

  if (isError && error instanceof RoadSenseLifecycleNotFoundError) {
    return (
      <Message title="Report not found" body="No RoadSense report exists for this id. It may have been deleted." />
    );
  }

  if (isError && error instanceof RoadSenseLifecycleNotReadyError) {
    const detail =
      error.status === "failed" ?
        "Report is unavailable at the moment. Please reload in a few minutes."
      : "Report is being generated. Please reload in a few minutes.";
    return <Message title="Report not ready" body={detail} />;
  }

  if (isError) {
    return (
      <Message
        title="Couldn't load sites"
        body="Something went wrong loading this report's sites. Please refresh the page to try again."
      />
    );
  }

  if (sites.length === 0) {
    return <Message title="No sites" body="This report doesn't cover any sites." />;
  }

  return (
    <View gap="md">
      {sites.map((site) => (
        <ReportsListItem
          key={site.siteId}
          title={site.siteName}
          href={`/tenants/${tenantId}/reports/roadsense/report/${encodeURIComponent(reportId)}/site/${encodeURIComponent(site.siteId)}`}
        >
          <ReportsListItemText>View report</ReportsListItemText>
        </ReportsListItem>
      ))}
    </View>
  );
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <View gap="sm" px="md">
      <Text textStyle="lg/semibold" color="text">
        {title}
      </Text>
      <Text textStyle="sm/regular" color="textMuted">
        {subtitle}
      </Text>
    </View>
  );
}

function Message({ title, body }: { title: string; body: string }) {
  return (
    <View padding="3xl" alignItems="center" gap="sm">
      <Text textStyle="sm/semibold" color="text" textAlign="center">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted" textAlign="center">
        {body}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create(() => ({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    maxWidth: sizes.webContentMaxWidth,
    width: "100%",
  },
}));
